import React, { useEffect, useState, useRef, useCallback, useImperativeHandle, forwardRef } from 'react';
import toast from 'react-hot-toast';
import { useTenant } from '../../context/TenantContext';
import { useAuth } from '../../context/AuthContext';
import {
  internalTaskService, InternalTask, InternalTaskHistoryItem,
} from '../../services/internalTaskService';
import { internalTaskTypeService, InternalTaskType } from '../../services/internalTaskTypeService';
import { userService, User } from '../../services/userService';
import { meetingService, Meeting } from '../../services/meetingService';
import { noteService, Note } from '../../services/noteService';

interface InternalTaskModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  patientId: number;          // required for create
  task?: InternalTask | null; // present for edit
  onOpenEditPatient?: () => void;
  onOpenNewMeeting?: () => void;
  onOpenSummary?: (meeting: Meeting) => void;
}

// ---- date helpers --------------------------------------------------------
const pad = (n: number) => String(n).padStart(2, '0');

const fmtDateTime = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fmtRelative = (iso: string) => {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  if (diffMin < 1 && diffMs >= 0) return 'Now';
  const isToday = d.getDate() === now.getDate() && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  if (isToday) return `Today ${time}`;
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${time}`;
};

const isHebrew = (text: string) => /[֐-׿]/.test(text);

const POPUP_INPUT = 'w-full px-4 py-2 rounded-lg bg-[#EBF5F5] border border-[#B4D9D4] focus:outline-none focus:ring-2 focus:ring-[#366A75] focus:border-transparent';

const PRIORITIES = ['Low', 'Medium', 'High', 'Urgent'];

// ===========================================================================
// Inline chat-style notes (reuses noteService) — matches clinicui NotesList.
// ===========================================================================
interface TaskNotesRef { submitPendingNote: () => Promise<void>; }
interface TaskNotesProps { taskId: number; currentUserId?: number; }

const TaskNotes = forwardRef<TaskNotesRef, TaskNotesProps>(({ taskId, currentUserId }, ref) => {
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editText, setEditText] = useState('');
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    try {
      const data = await noteService.getNotes('internal_task', taskId);
      setNotes([...data].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()));
    } catch {
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);
  useEffect(() => { if (!loading && scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [notes, loading]);

  const addNote = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await noteService.createNote({ entity_type: 'internal_task', entity_id: taskId, text: text.trim() });
      setText('');
      await fetchNotes();
    } catch {
      toast.error('Failed to create note');
    } finally {
      setSaving(false);
    }
  };

  useImperativeHandle(ref, () => ({
    submitPendingNote: async () => { if (text.trim()) await addNote(); },
  }));

  const saveEdit = async (id: number) => {
    if (!editText.trim()) return;
    setSaving(true);
    try {
      await noteService.updateNote(id, { text: editText.trim() });
      setEditingId(null);
      setEditText('');
      await fetchNotes();
    } catch {
      toast.error('Failed to update note');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div
        ref={scrollRef}
        className="flex flex-col gap-3 rounded-lg p-3 overflow-y-auto"
        style={{ minHeight: '140px', maxHeight: '280px', backgroundColor: '#C8E6E3' }}
      >
        {loading && <div className="text-sm text-gray-600 italic text-center py-2">Loading notes...</div>}
        {!loading && notes.length === 0 && <div className="text-sm text-gray-500 italic text-center py-4">No notes yet</div>}
        {notes.map((note) => {
          const isOwn = !!currentUserId && note.created_by === currentUserId;
          const rtl = isHebrew(note.text);
          const isEditing = editingId === note.id;
          const isHovered = hoveredId === note.id;
          return (
            <div
              key={note.id}
              className="w-full flex flex-col"
              onMouseEnter={() => setHoveredId(note.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              <div className={`flex items-center gap-1 mb-1 text-xs text-gray-600 ${isOwn ? 'flex-row-reverse' : ''}`}>
                <span className="font-semibold">{note.created_by_name || 'Unknown'}</span>
                <span className="font-normal">{fmtRelative(note.created_at)}</span>
              </div>
              {isEditing ? (
                <div className="flex gap-2 w-full max-w-[90%]">
                  <textarea
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    className="flex-1 px-2 py-1 text-sm border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    style={{ minHeight: '60px', direction: rtl ? 'rtl' : 'ltr' }}
                    autoFocus
                    disabled={saving}
                  />
                  <div className="flex flex-col gap-1">
                    <button onClick={() => saveEdit(note.id)} disabled={saving || !editText.trim()} className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50" title="Save">✓</button>
                    <button onClick={() => { setEditingId(null); setEditText(''); }} disabled={saving} className="px-2 py-1 text-xs bg-gray-300 text-gray-700 rounded hover:bg-gray-400 disabled:opacity-50" title="Cancel">✕</button>
                  </div>
                </div>
              ) : (
                <div className={`flex items-center gap-1 w-full ${isOwn ? 'justify-end' : 'justify-start'}`}>
                  {isOwn && (
                    <button
                      onClick={() => { setEditingId(note.id); setEditText(note.text); }}
                      className={`text-gray-500 hover:text-blue-600 text-sm p-0.5 flex-shrink-0 transition-opacity ${isHovered ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                      title="Edit note"
                    >✎</button>
                  )}
                  <div
                    className={`max-w-[90%] px-3 py-2 text-sm leading-normal shadow-sm ${isOwn ? 'bg-white text-gray-800 rounded-2xl rounded-tr-sm' : 'bg-[#D9D9D9] text-gray-800 rounded-2xl rounded-tl-sm'}`}
                    style={rtl ? { direction: 'rtl' } : undefined}
                  >
                    {note.text}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex gap-2 items-end">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addNote(); } }}
          placeholder="Write a note… (Enter to send, Shift+Enter for new line)"
          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#366A75] resize-none"
          style={{ minHeight: '40px', maxHeight: '100px' }}
          disabled={saving}
          rows={1}
        />
        <button
          onClick={addNote}
          disabled={saving || !text.trim()}
          className="px-4 py-2 text-sm bg-[#366A75] text-white rounded-lg hover:bg-[#2d5a63] disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
        >
          {saving ? '…' : 'Send'}
        </button>
      </div>
    </div>
  );
});
TaskNotes.displayName = 'TaskNotes';

// ===========================================================================
// Main modal
// ===========================================================================
const InternalTaskModal: React.FC<InternalTaskModalProps> = ({
  open, onClose, onSaved, patientId, task, onOpenEditPatient, onOpenNewMeeting, onOpenSummary,
}) => {
  const { currentTenant } = useTenant();
  const { user } = useAuth();
  const currentUserId = user?.id;
  const isEdit = !!task;

  const [activeTab, setActiveTab] = useState<'task' | 'history'>('task');

  const [taskTypes, setTaskTypes] = useState<InternalTaskType[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [meetings, setMeetings] = useState<Meeting[]>([]);

  const [taskTypeId, setTaskTypeId] = useState<number | ''>('');
  const [assignTo, setAssignTo] = useState<number[]>([]);
  const [priority, setPriority] = useState<string>('Low');
  const [statusVal, setStatusVal] = useState<string>('New');
  const [description, setDescription] = useState('');
  const [meetingId, setMeetingId] = useState<number | ''>('');
  const [saving, setSaving] = useState(false);

  // assignee searchable dropdown
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const userDropdownRef = useRef<HTMLDivElement>(null);

  // action split-button dropdown
  const [actionDropdownOpen, setActionDropdownOpen] = useState(false);
  const actionDropdownRef = useRef<HTMLDivElement>(null);

  // history
  const [history, setHistory] = useState<InternalTaskHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const notesRef = useRef<TaskNotesRef>(null);
  const autoUpdatedRef = useRef<Set<number>>(new Set());

  const selectedType = taskTypes.find((t) => t.id === taskTypeId);

  // ---- load reference data + prefill --------------------------------------
  useEffect(() => {
    if (!open || !currentTenant) return;
    internalTaskTypeService.getAll(currentTenant.id).then(setTaskTypes).catch(() => {});
    userService.getUsers(currentTenant.id).then(setUsers).catch(() => {});
    meetingService.getMeetings(currentTenant.id, patientId)
      .then((all) => setMeetings(all.filter((m) => m.status === 'done')))
      .catch(() => {});

    setActiveTab('task');
    if (task) {
      setTaskTypeId(task.task_type_id ?? '');
      setAssignTo(task.assign_to || []);
      setPriority(task.priority || 'Low');
      setDescription(task.description || '');
      setMeetingId(task.meeting_id ?? '');

      // Auto-move a New task to In Progress when the current user is an assignee.
      const userIsAssigned = currentUserId && (task.assign_to || []).includes(currentUserId);
      if (userIsAssigned && task.status === 'New' && !autoUpdatedRef.current.has(task.id)) {
        autoUpdatedRef.current.add(task.id);
        setStatusVal('In Progress');
        internalTaskService.update(task.id, { status: 'In Progress' })
          .then(() => onSaved())
          .catch(() => autoUpdatedRef.current.delete(task.id));
      } else {
        setStatusVal(task.status || 'New');
      }
    } else {
      setTaskTypeId('');
      setAssignTo([]);
      setPriority('Low');
      setStatusVal('New');
      setDescription('');
      setMeetingId('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentTenant, patientId, task]);

  // ---- history fetch ------------------------------------------------------
  useEffect(() => {
    if (!open || activeTab !== 'history' || !task) return;
    setHistoryLoading(true);
    internalTaskService.getHistory(task.id)
      .then(setHistory)
      .catch(() => setHistory([]))
      .finally(() => setHistoryLoading(false));
  }, [open, activeTab, task]);

  // ---- click-outside for dropdowns ----------------------------------------
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(e.target as Node)) setDropdownOpen(false);
      if (actionDropdownRef.current && !actionDropdownRef.current.contains(e.target as Node)) setActionDropdownOpen(false);
    };
    if (dropdownOpen || actionDropdownOpen) {
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }
  }, [dropdownOpen, actionDropdownOpen]);

  const handleTypeChange = (id: number) => {
    setTaskTypeId(id);
    if (!isEdit) {
      const t = taskTypes.find((x) => x.id === id);
      if (t && t.default_assignee_ids && t.default_assignee_ids.length) setAssignTo(t.default_assignee_ids);
    }
  };

  const toggleUser = (id: number) => {
    // Keep the dropdown open for multi-select; the selected user jumps to the top
    // (with a ✓). The dropdown closes on click-outside.
    setAssignTo((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
    setSearchQuery('');
  };

  const filteredUsers = (() => {
    const q = searchQuery.toLowerCase();
    return users
      .filter((u) => (u.full_name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q))
      .sort((a, b) => {
        const aSel = assignTo.includes(a.id); const bSel = assignTo.includes(b.id);
        if (aSel && !bSel) return -1;
        if (!aSel && bSel) return 1;
        return (a.full_name || '').localeCompare(b.full_name || '');
      });
  })();
  const selectedUsers = users.filter((u) => assignTo.includes(u.id));

  // ---- save ---------------------------------------------------------------
  const doSave = async (newStatus?: string) => {
    await notesRef.current?.submitPendingNote();
    if (!taskTypeId) { toast.error('Select a task type'); return; }
    const typeCanAutoAssign = !!selectedType && ((selectedType.default_assignee_ids?.length || 0) > 0 || !!selectedType.assign_type);
    if (!assignTo.length && !typeCanAutoAssign) { toast.error('Assign to at least one user'); return; }
    if (!description.trim()) { toast.error('Description is required'); return; }
    if (selectedType?.show_meeting_dropdown && !meetingId) { toast.error('Select a meeting'); return; }

    setSaving(true);
    try {
      const payload = {
        task_type_id: taskTypeId as number,
        assign_to: assignTo,
        priority,
        description: description.trim(),
        meeting_id: (selectedType?.show_meeting_dropdown || task?.meeting_id) && meetingId ? (meetingId as number) : null,
      };
      if (isEdit && task) {
        await internalTaskService.update(task.id, { ...payload, status: newStatus || statusVal });
        toast.success('Task updated');
      } else {
        await internalTaskService.create({ patient_id: patientId, ...payload });
        toast.success('Task created');
      }
      onSaved();
      onClose();
    } catch {
      toast.error('Could not save task');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!task) return;
    if (!window.confirm('Delete this task?')) return;
    setSaving(true);
    try {
      await internalTaskService.remove(task.id);
      toast.success('Task deleted');
      onSaved();
      onClose();
    } catch {
      toast.error('Could not delete task');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  // ---- action split-button logic ------------------------------------------
  const selectedMeeting = meetings.find((m) => m.id === meetingId);
  const openPatientPage = () => window.open(`/patients/${patientId}`, '_blank');
  const runMeetingSummary = () => { if (selectedMeeting && onOpenSummary) onOpenSummary(selectedMeeting); else openPatientPage(); };
  // clinicui show/hide logic: if a meeting is selected, Meeting Summary is the
  // default action; otherwise use the task type's default_action. set_meeting is
  // hidden for users who cannot schedule (all users can schedule in this app).
  const canScheduleMeeting = true;
  const rawAction = meetingId ? 'meeting_summary' : (selectedType?.default_action || null);
  const effectiveActionBase = rawAction === 'set_meeting' && !canScheduleMeeting ? null : rawAction;
  // Show the action button for every task in edit mode; default to Patient Page
  // when the task type has no configured default action.
  const effectiveAction = effectiveActionBase || 'patient_page';
  const actionLabel: Record<string, string> = {
    patient_page: 'Patient Page', edit_patient: 'Edit Patient', set_meeting: 'Set Meeting', meeting_summary: 'Meeting Summary',
  };
  const runAction = (action: string) => {
    if (action === 'patient_page') openPatientPage();
    else if (action === 'edit_patient') { onOpenEditPatient ? onOpenEditPatient() : openPatientPage(); }
    else if (action === 'set_meeting') { onOpenNewMeeting ? onOpenNewMeeting() : openPatientPage(); }
    else if (action === 'meeting_summary') runMeetingSummary();
  };

  // ---- workflow button visibility -----------------------------------------
  const isCreator = !!currentUserId && task?.created_by === currentUserId;
  const isAssigned = !!currentUserId && assignTo.includes(currentUserId);
  let showWaiting = false, showReopen = false, showFinish = false, showDone = false;
  if (isEdit) {
    if (isCreator) { if (statusVal === 'Done') showReopen = true; else showDone = true; }
    if (isAssigned && (statusVal === 'New' || statusVal === 'In Progress')) { showWaiting = true; showFinish = true; }
  }

  const wfBtn = 'px-4 py-2 bg-[#EBF5F5] text-[#3A7380] rounded-xl shadow-sm hover:bg-[#D0E7E5] transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium border-0 outline-none whitespace-nowrap';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1250] p-4" onClick={onClose}>
      <div
        className="bg-[#B4D9D4] rounded-3xl px-8 pb-8 w-full max-w-3xl shadow-2xl relative"
        style={{ maxHeight: '90vh', overflowY: 'auto', paddingBottom: '30px' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative pt-2 pb-2 flex items-center justify-center">
          <h2 className="text-2xl font-bold text-[#366A75]">{isEdit ? 'Update Task' : 'Create Task'}</h2>
          <button onClick={onClose} disabled={saving} className="absolute right-0 text-[#366A75] hover:text-[#2D5A60] transition-colors disabled:opacity-50">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs + meta */}
        <div className="flex items-center mb-4">
          <button
            onClick={() => setActiveTab('task')}
            className={`px-4 py-2 font-medium transition-all border ${activeTab === 'task' ? 'bg-[#3A7380] text-white border-[#3A7380]' : 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200'}`}
          >
            Task
          </button>
          {isEdit && (
            <button
              onClick={() => setActiveTab('history')}
              className={`px-4 py-2 font-medium transition-all border ${activeTab === 'history' ? 'bg-[#3A7380] text-white border-[#3A7380]' : 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200'}`}
            >
              History
            </button>
          )}
          {isEdit && task && (
            <div className="ml-6 flex items-start gap-[72px] text-sm">
              <div className="flex flex-col">
                <span className="font-medium text-gray-500">Created by:</span>
                <span className="font-bold text-gray-700">{task.created_by_name || 'Unknown'}</span>
              </div>
              <div className="flex flex-col">
                <span className="font-medium text-gray-500">Created:</span>
                <span className="font-bold text-gray-700">{fmtDateTime(task.created_at)}</span>
              </div>
              {task.updated_at && (
                <div className="flex flex-col">
                  <span className="font-medium text-gray-500">Updated:</span>
                  <span className="font-bold text-gray-700">{fmtDateTime(task.updated_at)}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Task Tab */}
        {activeTab === 'task' && (
          <div className="space-y-3 mb-6">
            {/* Task Type + Action split button */}
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-gray-700 w-24 flex-shrink-0">Task Type <span className="text-red-500">*</span></label>
              <select
                value={taskTypeId}
                onChange={(e) => handleTypeChange(parseInt(e.target.value, 10))}
                className={`${POPUP_INPUT} flex-1`}
                disabled={saving}
              >
                <option value="">Select a task type</option>
                {taskTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>

              {isEdit && effectiveAction && (
                <div className="relative" ref={actionDropdownRef}>
                  <div className="flex">
                    <button
                      type="button"
                      onClick={() => runAction(effectiveAction)}
                      disabled={saving}
                      className="px-4 py-2 bg-[#4C6EF5] text-white rounded-l-lg shadow hover:bg-[#3B5ED6] transition-all disabled:opacity-50 font-medium border-0 outline-none whitespace-nowrap"
                      style={{ minWidth: '120px' }}
                      title="Execute action"
                    >
                      {actionLabel[effectiveAction]}
                    </button>
                    <button
                      type="button"
                      onClick={() => setActionDropdownOpen((v) => !v)}
                      disabled={saving}
                      className="px-3 py-2 bg-[#4C6EF5] text-white rounded-r-lg shadow hover:bg-[#3B5ED6] transition-all disabled:opacity-50 font-medium border-l border-white/20 outline-none flex items-center justify-center"
                      title="Show all actions"
                    >
                      <svg width="12" height="8" viewBox="0 0 12 8" fill="none"><path d="M1 1.5L6 6.5L11 1.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    </button>
                  </div>
                  {actionDropdownOpen && (
                    <div className="absolute z-50 mt-1 w-full bg-white border border-[#B4D9D4] rounded-lg shadow-lg overflow-hidden">
                      <button type="button" onClick={() => { openPatientPage(); setActionDropdownOpen(false); }} className="w-full px-4 py-2 text-left text-sm hover:bg-[#EBF5F5] transition-colors text-gray-700">Patient Page</button>
                      <button type="button" onClick={() => { runAction('edit_patient'); setActionDropdownOpen(false); }} className="w-full px-4 py-2 text-left text-sm hover:bg-[#EBF5F5] transition-colors text-gray-700">Edit Patient</button>
                      {canScheduleMeeting && (
                        <button type="button" onClick={() => { runAction('set_meeting'); setActionDropdownOpen(false); }} className="w-full px-4 py-2 text-left text-sm hover:bg-[#EBF5F5] transition-colors text-gray-700">Set Meeting</button>
                      )}
                      {meetingId && (
                        <button type="button" onClick={() => { runMeetingSummary(); setActionDropdownOpen(false); }} className="w-full px-4 py-2 text-left text-sm hover:bg-[#EBF5F5] transition-colors text-gray-700">Meeting Summary</button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Assign To searchable dropdown */}
            <div ref={userDropdownRef}>
              <div className="flex items-start gap-4">
                <label className="text-sm font-medium text-gray-700 w-24 flex-shrink-0 pt-2">Assign To <span className="text-red-500">*</span></label>
                <div className="flex-1">
                  {selectedUsers.length > 0 && (
                    <div className="mb-2 flex flex-wrap gap-2">
                      {selectedUsers.map((u) => (
                        <div key={u.id} className="inline-flex items-center gap-1 px-2 py-1 bg-[#366A75] text-white rounded-md text-sm">
                          <span>{u.full_name}</span>
                          <button type="button" onClick={() => setAssignTo((prev) => prev.filter((x) => x !== u.id))} disabled={saving} className="hover:text-red-200 transition-colors">×</button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="relative">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => { setSearchQuery(e.target.value); setDropdownOpen(true); }}
                      onFocus={() => setDropdownOpen(true)}
                      placeholder="Search and select users..."
                      className={`${POPUP_INPUT} pr-9`}
                      disabled={saving}
                    />
                    <button
                      type="button"
                      onClick={() => setDropdownOpen((v) => !v)}
                      disabled={saving}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      title="Toggle list"
                      tabIndex={-1}
                    >
                      <svg className={`w-4 h-4 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {dropdownOpen && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-[#B4D9D4] rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {filteredUsers.length === 0 ? (
                          <div className="px-4 py-3 text-sm text-gray-500">No users found</div>
                        ) : (
                          <div className="py-1">
                            {filteredUsers.map((u) => {
                              const isSel = assignTo.includes(u.id);
                              return (
                                <button
                                  key={u.id}
                                  type="button"
                                  onClick={() => toggleUser(u.id)}
                                  className={`w-full px-4 py-2 text-left text-sm hover:bg-[#EBF5F5] transition-colors flex items-center justify-between ${isSel ? 'bg-[#EBF5F5] font-medium' : ''}`}
                                >
                                  <span className="text-gray-700">{u.full_name}</span>
                                  {isSel && <span className="text-[#366A75]">✓</span>}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Meeting dropdown */}
            {(selectedType?.show_meeting_dropdown || task?.meeting_id) && (
              <div className="flex items-center gap-4">
                <label className="text-sm font-medium text-gray-700 w-24 flex-shrink-0">Meeting <span className="text-red-500">*</span></label>
                <select
                  value={meetingId}
                  onChange={(e) => setMeetingId(e.target.value ? Number(e.target.value) : '')}
                  className={`${POPUP_INPUT} flex-1`}
                  disabled={saving}
                >
                  <option value="">Select a meeting</option>
                  {meetings.map((m) => {
                    const d = new Date(m.scheduled_at);
                    const date = `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
                    return <option key={m.id} value={m.id}>{date} {m.meeting_type?.name || m.title}</option>;
                  })}
                </select>
              </div>
            )}

            {/* Priority + Status */}
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-gray-700 w-24 flex-shrink-0">Priority <span className="text-red-500">*</span></label>
              <select value={priority} onChange={(e) => setPriority(e.target.value)} className={`${POPUP_INPUT} flex-1`} disabled={saving}>
                {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              {isEdit && (
                <>
                  <label className="text-sm font-medium text-gray-700 w-24 flex-shrink-0 text-right">Status</label>
                  <input type="text" value={statusVal} readOnly className={`${POPUP_INPUT} flex-1 bg-gray-100 cursor-not-allowed`} />
                </>
              )}
            </div>

            {/* Description */}
            <div className="flex items-start gap-4">
              <label className="text-sm font-medium text-gray-700 w-24 flex-shrink-0 pt-2">Description <span className="text-red-500">*</span></label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Add task description..."
                className={`${POPUP_INPUT} resize-none flex-1`}
                style={{ minHeight: '96px' }}
                disabled={saving}
              />
            </div>

            {/* Notes (edit only — needs a persisted task) */}
            {isEdit && task && (
              <div className="flex items-start gap-4">
                <label className="text-sm font-medium text-gray-700 w-24 flex-shrink-0 pt-1">Notes</label>
                <div className="flex-1 min-w-0">
                  <TaskNotes ref={notesRef} taskId={task.id} currentUserId={currentUserId} />
                </div>
              </div>
            )}
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="mb-6">
            <div className="border border-gray-300 rounded-lg overflow-hidden" style={{ minHeight: '300px', maxHeight: '500px' }}>
              {historyLoading ? (
                <div className="flex justify-center items-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400" /></div>
              ) : history.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No history available</div>
              ) : (
                <div className="overflow-auto" style={{ maxHeight: '500px' }}>
                  <table className="w-full border-collapse">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        <th className="border border-gray-300 px-3 py-2 text-left text-xs font-semibold text-gray-700" style={{ width: '150px' }}>Date</th>
                        <th className="border border-gray-300 px-3 py-2 text-left text-xs font-semibold text-gray-700" style={{ width: '120px' }}>User</th>
                        <th className="border border-gray-300 px-3 py-2 text-left text-xs font-semibold text-gray-700" style={{ width: '150px' }}>Action</th>
                        <th className="border border-gray-300 px-3 py-2 text-left text-xs font-semibold text-gray-700" style={{ width: '120px' }}>Type</th>
                        <th className="border border-gray-300 px-3 py-2 text-left text-xs font-semibold text-gray-700">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((h) => (
                        <tr key={h.id} className="hover:bg-gray-50">
                          <td className="border border-gray-300 px-3 py-2 text-xs text-gray-700 whitespace-nowrap">{fmtDateTime(h.date)}</td>
                          <td className="border border-gray-300 px-3 py-2 text-xs text-gray-700">{h.user_name || (h.user_id ? `User #${h.user_id}` : '-')}</td>
                          <td className="border border-gray-300 px-3 py-2 text-xs text-gray-700">{h.action}</td>
                          <td className="border border-gray-300 px-3 py-2 text-xs text-gray-700">{h.type || '-'}</td>
                          <td className="border border-gray-300 px-3 py-2 text-xs text-gray-700">{h.notes}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Buttons */}
        <div className="flex justify-center gap-2 flex-wrap">
          <button onClick={() => doSave()} disabled={saving} className={wfBtn}>{saving ? 'Saving...' : 'Save'}</button>
          {showWaiting && <button onClick={() => doSave('Waiting for Response')} disabled={saving} className={wfBtn}>Waiting for Response</button>}
          {showReopen && <button onClick={() => doSave('In Progress')} disabled={saving} className={wfBtn}>Re Open</button>}
          {showFinish && <button onClick={() => doSave('Finish')} disabled={saving} className={wfBtn}>Finish</button>}
          {showDone && <button onClick={() => doSave('Done')} disabled={saving} className={wfBtn}>Task Completed</button>}
          {isCreator && statusVal === 'New' && (
            <button onClick={handleDelete} disabled={saving} className="px-4 py-2 bg-red-100 text-red-600 rounded-xl shadow-sm hover:bg-red-200 transition-all disabled:opacity-50 font-medium border-0 outline-none">Delete Task</button>
          )}
          <button onClick={onClose} disabled={saving} className={wfBtn}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

export default InternalTaskModal;
